<script lang="ts" setup>
import { type PropType, ref, watchEffect } from 'vue';
import { CaseListElement, getContainer } from '@dandelion/core';
import { CaseListServiceInterface } from '@dandelion/core';
import type { CaseServiceInterface } from '@dandelion/core';
import type { CurrentCaseServiceInterface } from '@dandelion/core';
import type TranslationService from '@dandelion/core/src/services/TranslationService';
import { Contexts } from '@dandelion/core';
import { MagicNumbers } from '@dandelion/core';
import { NamedBindings } from '@dandelion/core';
import { TypesOfActionHooks } from '@dandelion/core';
import Helpers from '../../utils/Helpers';
import { ReadmissionStorageInterface } from '../../interfaces/ReadmissionStorageInterface';
import ReadmissionHashChange from '../../entities/ReadmissionHashChange';

const props = defineProps({
  context: {
    type: String as PropType<string>,
    required: false,
    default: Contexts.DEFAULT,
  },
  deletingCaseID: {
    type: String as PropType<string>,
    required: true,
    default: '',
  },
  rowElement: {
    type: CaseListElement as PropType<CaseListElement>,
    required: true,
    default: '',
  },
  deletionDialogVisibility: {
    type: Boolean as PropType<boolean>,
    required: true,
    default: false,
  },
});

const container = getContainer(props.context);
const ts = container.get(NamedBindings.TranslationService) as TranslationService;
const caseListService = container.get(NamedBindings.CaseListService) as CaseListServiceInterface;
const caseService = container.get(NamedBindings.CaseService) as CaseServiceInterface;
const currentCaseService = container.get(NamedBindings.CurrentCaseService) as CurrentCaseServiceInterface;

const deleteDialogRef = ref<HTMLDialogElement | null>(null);
const emit = defineEmits(['close-deletion', 'show-perma-deletion']);

watchEffect(() => {
  if (deleteDialogRef.value) {
    props.deletionDialogVisibility ? deleteDialogRef.value.showModal() : deleteDialogRef.value.close();
  }
});

async function deleteCase() {
  await Helpers.runHook(props.context, TypesOfActionHooks.BeforeDeleting, props.deletingCaseID);
  const eventDate = new Date();
  if (currentCaseService.getCurrentCase().value == props.deletingCaseID) {
    currentCaseService.terminateEditing();
  }
  await caseService.deleteCase(props.deletingCaseID);
  await caseListService.removeCase(props.deletingCaseID, eventDate);
  emit('close-deletion');
}

function showTotalRemoval() {
  emit('show-perma-deletion');
  emit('close-deletion');
}
</script>

<template>
  <dialog ref="deleteDialogRef" class="deletion-dialog dialog">
    <span
      class="deletion-dialog-msg"
      v-html="
        `${ts.t('You really want to delete the case')}: ${deletingCaseID.substring(
          0,
          15
        )+ ' - '+ props.rowElement?.cache?.get('core.personalData.name') + ' ' + props.rowElement?.cache?.get('core.personalData.surname') }?`
      "
    />
    <section class="delete-dialog-buttons-section dialog-buttons-section">
      <button
        type="button"
        @click="deleteCase"
        class="delete-dialog-btn delete-case-btn primary"
        v-html="ts.t('Delete')"
      />
      <button
        type="button"
        @click="showTotalRemoval"
        class="delete-dialog-btn open-perma-deletion-dialog-btn primary"
        v-html="ts.t('Permanently delete')"
      />
      <button
        type="button"
        @click="$emit('close-deletion')"
        class="delete-dialog-btn cancel-deletion-btn secondary"
        v-html="ts.t('Cancel')"
      />
    </section>
  </dialog>
</template>

<style>
.delete-case-dialog:modal {
  display: flex;
  flex-direction: column;
}

.delete-case-dialog-buttons-section {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
}
</style>
